import { useEffect, useRef, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export const FIELD_TYPE_LABELS = {
  text: "Short text", textarea: "Long text", number: "Number", date: "Date", select: "Dropdown",
  radio: "Multiple choice", checkboxes: "Tick boxes", yes_no: "Yes / No", phone: "Phone", email: "Email", file: "File upload",
};
export const CHOICE_TYPES = ["select", "radio", "checkboxes"];
export const WIDE_TYPES = ["textarea", "checkboxes", "radio", "file"];

export const fileHref = (path) => `${API}/files/${path}?auth=${localStorage.getItem("token")}`;

const CORE_DOC = { sex: "sex", marital_status: "marital_status", facility_type: "facility_type", dues: "pays_dues",
  previous_employment: "worked_at_previous_facility", mother_status: "mother_status", father_status: "father_status" };
export const isVisible = (field, form) => {
  const rule = field.show_if;
  if (!rule) return true;
  const v = CORE_DOC[rule.field] ? form[CORE_DOC[rule.field]] : (form.custom_fields || {})[rule.field];
  return Array.isArray(v) ? v.some((x) => rule.values.includes(x)) : rule.values.includes(v);
};

export const visibleAnswers = (layout, form) => {
  const eff = { ...(form.custom_fields || {}) };
  (layout?.sections || []).flatMap((s) => s.fields).forEach((f) => {
    if (!f.core && !isVisible(f, { ...form, custom_fields: eff })) delete eff[f.key];
  });
  return eff;
};

export const useFormLayout = (memberType) => {
  const [layout, setLayout] = useState(null);
  useEffect(() => {
    api.get(`/forms/${memberType || "public"}`).then(({ data }) => setLayout(data)).catch(() => setLayout(null));
  }, [memberType]);
  return layout;
};

function FileInput({ value, onChange, disabled, testId }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/form-file", fd);
      onChange(data.path);
      toast.success("File uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex flex-wrap items-center gap-3">
      {value ? <a href={fileHref(value)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary underline" data-testid={`${testId}-link`}><Paperclip size={15} />View uploaded file</a>
        : <span className="text-sm text-muted-foreground">No file uploaded</span>}
      {!disabled && <>
        <input ref={ref} type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={upload} data-testid={`${testId}-input`} />
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-upload`}>
          <UploadSimple size={15} />{busy ? "Uploading…" : value ? "Replace" : "Upload"}
        </Button>
      </>}
    </div>
  );
}

function Control({ field, value, onChange, disabled, testId }) {
  const t = field.type;
  if (t === "textarea") return <Textarea data-testid={testId} value={value || ""} disabled={disabled} maxLength={3000} onChange={(e) => onChange(e.target.value)} />;
  if (t === "select" || t === "yes_no") {
    const opts = t === "yes_no" ? [["yes", "Yes"], ["no", "No"]] : field.options.map((o) => [o, o]);
    return (
      <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (t === "radio" || t === "checkboxes") {
    const list = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-x-5 gap-y-2" role="group" data-testid={testId}>
        {field.options.map((o, i) => {
          const on = t === "radio" ? value === o : list.includes(o);
          const toggle = () => onChange(t === "radio" ? o : on ? list.filter((x) => x !== o) : [...list, o]);
          return (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={on} onCheckedChange={toggle} disabled={disabled} className={t === "radio" ? "rounded-full" : ""} data-testid={`${testId}-opt-${i}`} />{o}
            </label>
          );
        })}
      </div>
    );
  }
  if (t === "file") return <FileInput value={value} onChange={onChange} disabled={disabled} testId={testId} />;
  const types = { number: "number", date: "date", phone: "tel", email: "email" };
  return <Input data-testid={testId} type={types[t] || "text"} value={value || ""} disabled={disabled} maxLength={t === "text" ? 500 : undefined} onChange={(e) => onChange(e.target.value)} />;
}

export function CustomField({ field, value, onChange, disabled, error, testPrefix = "custom" }) {
  const testId = `${testPrefix}-${field.key}`;
  return (
    <div className={`space-y-1.5 ${WIDE_TYPES.includes(field.type) ? "sm:col-span-2" : ""}`}>
      <Label>{field.label}{field.required && <span className="text-destructive"> *</span>}</Label>
      <Control field={field} value={value} onChange={onChange} disabled={disabled} testId={testId} />
      {field.help && <p className="text-xs text-muted-foreground">{field.help}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
